// Live-preview plumbing for the block editor (motiflink §2.4, M4 design note §5.5).
//
// The marketing site owns a chromeless `/preview/block` route that decodes a
// block payload out of the query string and renders it through the REAL
// BlockRenderer with the real site CSS. That is what makes the preview
// pixel-accurate for free: there is no second renderer to keep in sync.

/** Marketing site origin matching this admin deployment. Mirrors `appOrigin()`. */
export function marketingOrigin(): string {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'admin.fajrarabi.com') return 'https://fajrarabi.com';
    if (host === 'staging-admin.fajrarabi.com') return 'https://staging.fajrarabi.com';
  }
  return (import.meta.env.VITE_MARKETING_BASE as string | undefined) ?? 'http://localhost:5173';
}

/**
 * URL-safe base64 of a UTF-8 string.
 *
 * `btoa` throws on any code point above U+00FF, so Arabic copy — which is most
 * of what this CMS holds — cannot go through it directly. Encode to UTF-8
 * bytes first, map those bytes to a Latin-1 string, then base64 it. The `+/`
 * alphabet is swapped for `-_` so the result survives a query string without
 * percent-encoding (a raw `+` would decode back as a space).
 */
export function encodeBase64Url(input: string): string {
  const bytes = new TextEncoder().encode(input);
  let binary = '';
  // Chunked so a very long rich_text body can't blow the argument limit of
  // String.fromCharCode(...spread).
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_');
}

export interface PreviewPayload {
  type: string;
  content: Record<string, unknown>;
  settings: Record<string, unknown>;
  translations: Record<string, Record<string, unknown>>;
}

/** The iframe src for one block in one language. */
export function blockPreviewUrl(payload: PreviewPayload, lang: 'ar' | 'en'): string {
  const data = encodeBase64Url(JSON.stringify(payload));
  return `${marketingOrigin().replace(/\/+$/, '')}/preview/block?data=${data}&lang=${lang}`;
}

/** Preview viewport widths — desktop, tablet, phone. */
export const PREVIEW_VIEWPORTS = [
  { value: '1280', label: 'Desktop', width: 1280 },
  { value: '768', label: 'Tablet', width: 768 },
  { value: '390', label: 'Phone', width: 390 },
] as const;

export type PreviewViewport = (typeof PREVIEW_VIEWPORTS)[number]['value'];
